import { Formatting, IArticleImage, IMarkup, IParagraph, TitleStyle } from '@pressreader/content-types';
import { synthesizeLinkMarkups } from '@pressreader/content-utils';
import { getImageUrl } from '@pressreader/images';

import { IArticleDto } from '../types/article';
import { AnyParagraphDto } from '../types/paragraphs/any.paragrpah';
import { FormattingDto } from '../types/paragraphs/formatting';
import { IMarkupDto } from '../types/paragraphs/markup';
import { ITextParagraphDto } from '../types/paragraphs/text.paragraph';
import { ITitleParagraphDto } from '../types/paragraphs/title.paragraph';
import { TitleStyleDto } from '../types/paragraphs/title.style';

import { IArticleMapperContext } from './article.mapper';

const formattingMap: Record<FormattingDto, Formatting> = {
    [FormattingDto.Bold]: Formatting.Bold,
    [FormattingDto.Html]: Formatting.None,
    [FormattingDto.Italic]: Formatting.Italic,
    [FormattingDto.Link]: Formatting.Link,
    [FormattingDto.Lowercase]: Formatting.Lowercase,
    [FormattingDto.None]: Formatting.None,
    [FormattingDto.Quote]: Formatting.Quote,
    [FormattingDto.Uppercase]: Formatting.Uppercase,
};

function mapFormatting(formattingDto: FormattingDto) {
    return formattingMap[formattingDto] ?? Formatting.None;
}

const titleStyleMap: Record<TitleStyleDto, TitleStyle> = {
    [TitleStyleDto.None]: TitleStyle.None,
    [TitleStyleDto.Subtitle]: TitleStyle.Subtitle,
    [TitleStyleDto.SubtitleEmphasis]: TitleStyle.SubtitleEmphasis,
    [TitleStyleDto.Title]: TitleStyle.Title,
};

function mapTitleStyle(styleDto: TitleStyleDto) {
    return titleStyleMap[styleDto];
}

function mapMarkup(markupDto: IMarkupDto): IMarkup {
    return {
        start: markupDto.start,
        end: markupDto.end,
        formatting: mapFormatting(markupDto.formatting),
        attributes: markupDto.attributes,
    };
}

/**
 * Maps paragraph markups, synthesizing link markups when the server sent none.
 * @param paragraphDto - Source paragraph; `markups: null` means the server provided none.
 * @returns Mapped markups.
 */
function mapParagraphMarkups(paragraphDto: ITextParagraphDto | ITitleParagraphDto): IMarkup[] {
    if (paragraphDto.markups) {
        return paragraphDto.markups.map(mapMarkup);
    }

    return synthesizeLinkMarkups(paragraphDto.text);
}

function mapParagraph(
    context: IArticleMapperContext,
    paragraphDto: AnyParagraphDto,
    articleDto: IArticleDto,
    images: IArticleImage[],
): IParagraph | null {
    const { imagesSupported } = context;

    if (paragraphDto.type === 'text') {
        return {
            type: 'text',
            text: paragraphDto.text,
            markups: mapParagraphMarkups(paragraphDto),
        };
    } else if (paragraphDto.type === 'title') {
        return {
            type: 'title',
            text: paragraphDto.text,
            markups: mapParagraphMarkups(paragraphDto),
            style: mapTitleStyle(paragraphDto.style),
        };
    } else if (paragraphDto.type === 'image') {
        if (!imagesSupported) {
            return null;
        }

        let image: IArticleImage | null = null;
        if (articleDto.issue === null) {
            image = {
                id: paragraphDto.imageId,
                url: getImageUrl(paragraphDto.imageId),
                size: null,
            };
        } else {
            image = images.find(img => img.id === paragraphDto.imageId) ?? null;
        }

        return {
            type: 'image',
            image,
            caption: paragraphDto.caption,
        };
    } else if (paragraphDto.type === 'opinion') {
        return {
            type: 'opinion',
            opinionId: paragraphDto.opinionId,
        };
    } else if (paragraphDto.type === 'video') {
        return {
            type: 'video',
            url: paragraphDto.url,
            caption: paragraphDto.caption,
            thumbnail: paragraphDto.thumbnail,
            height: paragraphDto.height,
            width: paragraphDto.width,
        };
    } else {
        throw new Error(`Paragraph cannot be mapped. Type ${(paragraphDto as IParagraph).type} is not supported.`);
    }
}

export { mapParagraph };
